import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Building2, TrendingUp, Users, ChevronRight, ArrowLeft, Loader2,
  PoundSterling, ShieldCheck, Calendar, ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/** /group — read-only group dashboard for the Group Aftersales Manager.
 *
 *  Shows:
 *   1. Group-wide KPI tiles (approved £, jobs, conversion, plans sold)
 *   2. Per-dealer league table with drill-in links
 *
 *  Access: gated to `user.is_group_manager || user.platform_owner`.
 *  Platform owners pick which group to view via a dropdown.
 */
export default function GroupDashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Resolve which group(s) we have access to
  useEffect(() => {
    api.get("/group/state")
      .then((r) => {
        const d = r.data;
        if (d.is_group_manager) {
          setGroups([d.group]);
          setSelectedGroupId(d.group?.id);
        } else if (d.platform_owner) {
          setGroups(d.groups || []);
          if (d.groups?.length) setSelectedGroupId(d.groups[0].id);
        } else {
          // Not allowed
          toast.error("You don't have access to a group dashboard");
          nav("/dashboard");
        }
      })
      .catch(() => toast.error("Couldn't load group state"))
      .finally(() => setLoading(false));
  }, [nav]);

  // Fetch the summary whenever the selected group changes
  useEffect(() => {
    if (!selectedGroupId) return;
    setSummary(null);
    const qs = user?.platform_owner ? `?group_id=${selectedGroupId}` : "";
    api.get(`/group/summary${qs}`)
      .then((r) => setSummary(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load group summary"));
  }, [selectedGroupId, user]);

  const period = summary?.period;
  const totals = summary?.totals;
  const dealers = summary?.dealers || [];

  const monthLabel = useMemo(() => {
    if (!period) return "";
    const d = new Date(period.from + "T00:00:00Z");
    return d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  }, [period]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!groups.length) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="card-default p-8 max-w-md text-center">
          <Building2 className="w-10 h-10 text-muted-foreground mx-auto" />
          <h1 className="font-display font-bold text-2xl mt-3">No groups yet</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Create a dealer group from the <Link to="/accounts" className="text-primary hover:underline">Accounts</Link> page, then assign workshops to it.
          </p>
          <Link to="/dashboard" className="btn-outline mt-5" data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/dashboard" className="btn-outline" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="hidden md:block min-w-0">
              <div className="label-overline text-primary">Group dashboard</div>
              <div className="font-display font-bold text-lg leading-tight truncate">
                {summary?.group?.name || "—"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.platform_owner && groups.length > 1 && (
              <select
                value={selectedGroupId || ""}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className="input-base text-sm"
                data-testid="group-selector"
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            )}
            <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" /> {monthLabel}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl xl:max-w-[1500px] 2xl:max-w-[1720px] mx-auto px-6 py-8 space-y-8">
        {/* Group-level KPI tiles */}
        <section data-testid="group-kpi-tiles" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiTile
            label="Approved value"
            value={totals ? `£${totals.approved_total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}` : "—"}
            sub={`Across ${totals?.dealer_count || 0} dealer${totals?.dealer_count === 1 ? "" : "s"}`}
            icon={<PoundSterling className="w-5 h-5" />}
            accent="primary"
            testId="group-kpi-approved"
          />
          <KpiTile
            label="Jobs authorised"
            value={totals?.job_count?.toLocaleString() ?? "—"}
            sub={totals ? `${totals.sent_count} sent · ${totals.conversion_pct}% conv.` : ""}
            icon={<TrendingUp className="w-5 h-5" />}
            accent="success"
            testId="group-kpi-jobs"
          />
          <KpiTile
            label="Red items recovered"
            value={totals?.red_recovered?.toLocaleString() ?? "—"}
            sub="Customer agreed to safety work"
            icon={<ShieldCheck className="w-5 h-5" />}
            accent="warning"
            testId="group-kpi-red"
          />
          <KpiTile
            label="Service plans sold"
            value={totals?.plans_sold?.toLocaleString() ?? "—"}
            sub="Via AI pitch loop"
            icon={<Users className="w-5 h-5" />}
            accent="info"
            testId="group-kpi-plans"
          />
        </section>

        {/* Per-dealer league table */}
        <section data-testid="group-dealers-section">
          <div className="flex items-end justify-between mb-3">
            <div>
              <h2 className="font-display font-bold text-2xl tracking-tight">Dealerships</h2>
              <p className="text-sm text-muted-foreground mt-1">Sorted by approved value this month — click any row to drill in.</p>
            </div>
          </div>

          {!summary && (
            <div className="card-default p-8 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mx-auto" />
            </div>
          )}

          {summary && dealers.length === 0 && (
            <div className="card-default p-8 text-center" data-testid="group-no-dealers">
              <Building2 className="w-10 h-10 text-muted-foreground mx-auto" />
              <h3 className="font-display font-bold text-lg mt-2">No dealerships in this group yet</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Assign workshops to <strong>{summary.group?.name}</strong> from the Accounts page.
              </p>
            </div>
          )}

          {summary && dealers.length > 0 && (
            <div className="card-default overflow-hidden" data-testid="group-dealers-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Dealer</th>
                      <th className="px-4 py-3 text-right font-semibold">Approved £</th>
                      <th className="px-4 py-3 text-right font-semibold hidden sm:table-cell">Jobs</th>
                      <th className="px-4 py-3 text-right font-semibold hidden md:table-cell">Sent</th>
                      <th className="px-4 py-3 text-right font-semibold">Conv. %</th>
                      <th className="px-4 py-3 text-right font-semibold hidden lg:table-cell">Plans sold</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {dealers.map((d, idx) => (
                      <tr
                        key={d.workshop_id}
                        className="hover:bg-muted/30 cursor-pointer"
                        onClick={() => nav(`/group/dealers/${d.workshop_id}`)}
                        data-testid={`group-dealer-row-${d.workshop_id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-muted text-[10px] font-bold grid place-items-center text-muted-foreground">
                              {idx + 1}
                            </span>
                            <div>
                              <div className="font-semibold leading-tight">{d.workshop_name}</div>
                              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">
                                {d.subscription_status || "trial"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-mono-plex font-semibold">
                          £{d.approved_total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-right font-mono-plex hidden sm:table-cell">{d.job_count}</td>
                        <td className="px-4 py-3 text-right font-mono-plex hidden md:table-cell text-muted-foreground">
                          {d.sent_count}
                        </td>
                        <td className="px-4 py-3 text-right font-mono-plex">
                          <span className={d.conversion_pct >= 60 ? "text-success" : d.conversion_pct >= 40 ? "text-warning" : "text-muted-foreground"}>
                            {d.conversion_pct}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono-plex hidden lg:table-cell">
                          {d.plans_sold ? <span className="text-primary font-semibold">{d.plans_sold}</span> : <span className="text-muted-foreground">0</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-muted-foreground">
                          <ChevronRight className="w-4 h-4 inline" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        {/* Read-only callout */}
        <section className="rounded-lg border border-border bg-muted/30 p-4 text-xs text-muted-foreground flex items-start gap-2.5" data-testid="group-readonly-callout">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong className="text-foreground">Read-only group view.</strong>{" "}
            You can see KPIs and recent activity across every dealership but cannot edit jobs, send quotes or take payments. To make changes, ask the relevant dealership's manager.
          </div>
        </section>
      </main>
    </div>
  );
}


function KpiTile({ label, value, sub, icon, accent = "primary", testId }) {
  const accentBg = {
    primary: "bg-primary/10 text-primary",
    success: "bg-success/10 text-success",
    warning: "bg-warning/10 text-warning",
    info: "bg-info/10 text-info",
  }[accent] || "bg-primary/10 text-primary";
  return (
    <div className="card-default p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="label-overline text-muted-foreground">{label}</div>
        <div className={`w-8 h-8 rounded-md grid place-items-center ${accentBg}`}>{icon}</div>
      </div>
      <div className="font-display font-black text-3xl tracking-tighter mt-2" data-testid={`${testId}-value`}>
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
